import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryOfflineAudio } from './api';

export interface UploadAudioUrlRequest {
  fileName: string;
  contentType: string;
  fileSize: number;
}

export interface UploadAudioUrlResponse {
  uploadUrl: string;
  recordingId: string;
  expiresIn: number;
}

export interface TranscriptionQuoteRequest {
  recordingId: string;
  consultationId: string;
}

export interface TranscriptionQuoteResponse {
  quoteId: string;
  quoteToken: string;
  durationSeconds: number;
  durationMinutes: number;
  billableMinutes: number;
  ratePerMinute: number;
  estimatedCost: number;
  currency: string;
  expiresAt: string;
}

export interface StartTranscriptionRequest {
  publicOrderId: string;
  quoteToken: string;
  consultationId: string;
  recordingId: string;
  metadata?: Record<string, unknown>;
}

export interface StartTranscriptionResponse {
  transcriptionId: string;
  status: string;
}

export interface TranscriptionStatusResponse {
  id: string;
  status: string;
  paymentStatus?: string;
  transcriptionStatus?: string;
  result?: unknown;
  errorMessage?: string;
  consultationId?: string | null;
  expiresAt?: string | null;
}

export interface ListTranscriptionsResponse {
  items: TranscriptionStatusResponse[];
  total: number;
  limit: number;
  offset: number;
}

export const consultationAudioApi = createApi({
  reducerPath: 'consultationAudioApi',
  baseQuery: baseQueryOfflineAudio,
  tagTypes: ['AudioTranscription'],
  endpoints: (build) => ({
    getUploadUrl: build.mutation<UploadAudioUrlResponse, UploadAudioUrlRequest>({
      query: (body) => ({
        url: '/transcriptions/upload-url',
        method: 'POST',
        body,
      }),
    }),
    createQuote: build.mutation<TranscriptionQuoteResponse, TranscriptionQuoteRequest>({
      query: (body) => ({
        url: '/transcriptions/quote',
        method: 'POST',
        body,
      }),
    }),
    startTranscription: build.mutation<StartTranscriptionResponse, StartTranscriptionRequest>({
      query: (body) => ({
        url: '/transcriptions/start',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AudioTranscription'],
    }),
    getTranscriptionById: build.query<TranscriptionStatusResponse, string>({
      query: (transcriptionId) => ({
        url: `/transcriptions/${transcriptionId}`,
        method: 'GET',
      }),
      providesTags: ['AudioTranscription'],
    }),
    getTranscriptions: build.query<ListTranscriptionsResponse, { consultationId: string; limit?: number }>({
      query: ({ consultationId, limit = 1 }) => ({
        url: '/transcriptions',
        method: 'GET',
        params: { consultationId, limit },
      }),
      providesTags: ['AudioTranscription'],
    }),
  }),
});

export const {
  useGetUploadUrlMutation,
  useCreateQuoteMutation,
  useStartTranscriptionMutation,
  useLazyGetTranscriptionByIdQuery,
  useGetTranscriptionsQuery,
} = consultationAudioApi;
